import {
    AnalyticsTracker,
    OrderFormProperties,
    orderEventProperties,
    OrderType
} from '@trade-platform/ui-shared';
import { ApiFormError } from '@trade-platform/ui-utils';

/**
 * The order-form events for the dynamic form page. The page is reused as the user moves between an
 * order's forms, and saving progress reloads the form, so a form counts as opened only when the
 * page wasn't already on it. Nothing is sent before a form has opened.
 */
export class OrderFormAnalytics {
    private form?: OrderFormProperties;
    private openedAt = 0;

    constructor(
        private readonly tracker: AnalyticsTracker,
        private readonly now: () => number = Date.now
    ) {}

    opened(
        order: { id: string; firm?: { id: string } | null },
        orderType: OrderType,
        formId: string,
        formStatus: string | undefined
    ): void {
        if (this.form?.order_id === order.id && this.form.form_id === formId) {
            return;
        }
        this.form = { ...orderEventProperties(order, orderType), form_id: formId };
        this.openedAt = this.now();
        this.tracker.track('order_form_opened', { ...this.form, form_status: formStatus });
    }

    submitted(isLastForm: boolean): void {
        if (this.form) {
            this.tracker.track('order_form_submitted', {
                ...this.form,
                seconds_on_form: Math.round((this.now() - this.openedAt) / 1000),
                is_last_form: isLastForm
            });
        }
    }

    continueBlocked(reason: 'incomplete' | 'invalid', progress: number): void {
        if (this.form) {
            this.tracker.track('order_form_continue_blocked', {
                ...this.form,
                reason,
                progress_pct: Math.round(progress)
            });
        }
    }

    serverRejected(action: 'continue' | 'save_progress', error: ApiFormError): void {
        if (this.form) {
            const messages = error.messages ?? [];
            this.tracker.track('order_form_server_rejected', {
                ...this.form,
                action,
                error_codes: distinct(messages.map(item => item.code)),
                field_paths: distinct(messages.map(item => item.path))
            });
        }
    }

    progressSaved(progress: number): void {
        if (this.form) {
            this.tracker.track('order_form_progress_saved', {
                ...this.form,
                progress_pct: Math.round(progress)
            });
        }
    }

    previewed(): void {
        if (this.form) {
            this.tracker.track('order_form_previewed', this.form);
        }
    }
}

function distinct(values: (string | undefined)[]): string[] {
    return [...new Set(values.filter((value): value is string => Boolean(value)))].sort();
}
